import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Play } from "lucide-react";
import { LazyYouTube } from "./LazyYouTube";

const PLAYLIST_ID = "PL-t8V4eW2g_s105kfxmh7Y5bSs472Dwha";

const VIDEOS = [
    { id: "3HAqafDBUDQ", title: "Top Ten", cohort: "Young Moguls AMP" },
    {
        id: "48wSmvMx5gA",
        title: "Anything Goes",
        cohort: "Young Moguls of AMP feat Jeaux",
    },
    { id: "-3eKJEHWjL4", title: "Soarin", cohort: "Class of 2023" },
    { id: "hUUCl43j_9I", title: "WE UP / LATER", cohort: "Young Moguls of DDP" },
];

export const Media = () => {
    const [activeId, setActiveId] = useState(VIDEOS[0].id);
    const active = VIDEOS.find((v) => v.id === activeId) ?? VIDEOS[0];

    const selectVideo = (id) => {
        setActiveId(id);
    };

    return (
        <section
            id="media"
            data-testid="media-section"
            className="relative py-12 md:py-16 pt-6 md:pt-8 pb-6 md:pb-8 bg-[#0A0A0A] overflow-hidden [contain:layout_paint] transform-gpu"
        >
            <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
            <div aria-hidden className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-2xl pointer-events-none" />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 relative">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-6">
                    <div className="max-w-3xl">
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 04 — Media
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            Studio Lights.
                            <br />
                            <span className="gold-text-gradient">
                                Lime Lights.
                            </span>
                        </h2>
                        <p className="mt-6 text-white/75 leading-relaxed text-base md:text-lg">
                            Young Moguls master music production, lyric
                            writing, and audio engineering utilizing
                            industry-standard tools including{" "}
                            <span className="text-[#D4AF37]">GarageBand</span>,{" "}
                            <span className="text-[#D4AF37]">Logic Pro X</span>,{" "}
                            <span className="text-[#D4AF37]">Suno</span>, and{" "}
                            <span className="text-[#D4AF37]">Splice</span>.
                            Through hands-on studio experience, they transform
                            creative concepts into professional, release-ready
                            tracks and incredible music visuals.
                        </p>
                    </div>
                    <a
                        href={`https://www.youtube.com/playlist?list=${PLAYLIST_ID}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="media-yt-link"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-white/70 hover:text-[#D4AF37] transition-colors"
                    >
                        Watch on YouTube
                        <ExternalLink size={14} />
                    </a>
                </div>

                {/* Featured video frame */}
                <div className="relative aspect-video w-full overflow-hidden border border-[#D4AF37]/30 bg-black mb-3 group">
                    <LazyYouTube
                        videoId={activeId}
                        playlistId={PLAYLIST_ID}
                        title={active.title}
                        testId="media-featured"
                    />
                </div>

                {/* Now playing strip */}
                <div className="flex items-center justify-between mb-6 text-xs uppercase tracking-[0.25em] text-white/55">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                            <span className="text-[#D4AF37] font-bold">
                                Now Playing
                            </span>
                        </span>
                        <span className="text-white/80 normal-case tracking-normal text-sm font-display uppercase tracking-tight">
                            "{active.title}"
                        </span>
                    </div>
                    <span className="hidden sm:block">
                        {VIDEOS.length} videos in playlist
                    </span>
                </div>

                {/* Thumbnail cycle row — real playlist videos */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12">
                    {VIDEOS.map((v, i) => {
                        const isActive = v.id === activeId;
                        return (
                            <motion.button
                                key={v.id}
                                whileHover={{ y: -3 }}
                                onClick={() => selectVideo(v.id)}
                                data-testid={`media-thumb-${i}`}
                                aria-label={`Play ${v.title}`}
                                className={`relative aspect-video overflow-hidden text-left group transition-all duration-300 ${
                                    isActive
                                        ? "border-2 border-[#D4AF37] gold-glow"
                                        : "border border-white/10 hover:border-[#D4AF37]/60"
                                }`}
                            >
                                <img
                                    src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                                    alt={v.title}
                                    loading="lazy"
                                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                                        isActive
                                            ? ""
                                            : "grayscale-[50%] group-hover:grayscale-0"
                                    }`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                                {/* Play overlay */}
                                {!isActive && (
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="h-10 w-10 rounded-full bg-[#D4AF37] grid place-items-center">
                                            <Play
                                                size={14}
                                                className="text-black ml-0.5"
                                            />
                                        </span>
                                    </div>
                                )}

                                {/* Active badge */}
                                {isActive && (
                                    <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[#D4AF37] text-black text-[9px] uppercase tracking-[0.2em] font-bold">
                                        Now
                                    </div>
                                )}

                                {/* Bottom label */}
                                <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                                    <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">
                                        {v.cohort}
                                    </div>
                                    <div className="font-display text-sm uppercase tracking-tight text-white mt-1 leading-tight line-clamp-1">
                                        {v.title}
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Studio pipeline icons removed */}
            </div>
        </section>
    );
};
